import { PluginState, PluginAction } from "./types";
import { MeshPluginActions } from "./actions";

//Initial State
export const initialState: PluginState = {
  pluginId: "",
  uiPages: [],
  uiData: [],
  selectedUiType: "Settings",
  definedVariables: [],
  isSavedSection: [
    { name: "info", isSaved: false },
    { name: "ui", isSaved: false },
    { name: "logic", isSaved: false },
  ],
  configurationData: [],
  selectedConfigData: null,
  selectedLogicName: "",
  configVariables: [],
  fieldName: "",
  fieldType: "select",
  fields: [],
  dropdownOptions: [],
  hasUnsavedFields: false,
  hasUnsavedHelpDocs: false,
  helpDocs: [],
  inputDefaultValue: "",
  savedSettings: {},
  showForm: false,
  openMapfieldsDialog: { open: false, requestId: null },
  hasUnsavedGeneralInfo: false,
  selectedFormIndex: null,
  selectedSettingsPageIndex: null,
  hasLogicPage: false,
  hasUIPage: false,
  hasTempConfigPage: false,
};

//Helper function to resolve payload (handles both direct values and updater functions)
const resolvePayload = <T>(payload: T | ((prev: T) => T), current: T): T => {
  return typeof payload === "function"
    ? (payload as (prev: T) => T)(current)
    : payload;
};

//MeshPlugin Reducer
export function pluginReducer(
  state: PluginState,
  action: PluginAction,
): PluginState {
  switch (action.type) {
    case MeshPluginActions.SET_PLUGIN_ID:
      return { ...state, pluginId: action.payload };

    case MeshPluginActions.SET_UI_PAGES:
      return { ...state, uiPages: action.payload };

    case MeshPluginActions.SET_UI_DATA:
      return { ...state, uiData: resolvePayload(action.payload, state.uiData) };

    case MeshPluginActions.SET_SELECTED_UI_TYPE:
      return { ...state, selectedUiType: action.payload };

    case MeshPluginActions.SET_DEFINED_VARIABLES:
      return {
        ...state,
        definedVariables: resolvePayload(
          action.payload,
          state.definedVariables,
        ),
      };

    case MeshPluginActions.SET_IS_SAVED_SECTION:
      return {
        ...state,
        isSavedSection: resolvePayload(action.payload, state.isSavedSection),
      };

    case MeshPluginActions.SET_CONFIGURATION_DATA:
      return {
        ...state,
        configurationData: resolvePayload(
          action.payload,
          state.configurationData,
        ),
      };

    case MeshPluginActions.SET_SELECTED_CONFIG_DATA:
      return {
        ...state,
        selectedConfigData: resolvePayload(
          action.payload,
          state.selectedConfigData,
        ),
      };

    case MeshPluginActions.SET_SELECTED_LOGIC_NAME:
      return {
        ...state,
        selectedLogicName: resolvePayload(
          action.payload,
          state.selectedLogicName,
        ),
      };

    case MeshPluginActions.SET_CONFIG_VARIABLES:
      return {
        ...state,
        configVariables: resolvePayload(action.payload, state.configVariables),
      };

    case MeshPluginActions.SET_FIELD_NAME:
      return {
        ...state,
        fieldName: resolvePayload(action.payload, state.fieldName),
      };

    case MeshPluginActions.SET_FIELD_TYPE:
      return {
        ...state,
        fieldType: resolvePayload(action.payload, state.fieldType),
      };

    case MeshPluginActions.SET_ACTION_FIELDS:
      return { ...state, fields: resolvePayload(action.payload, state.fields) };

    case MeshPluginActions.SET_DROPDOWN_OPTIONS:
      return {
        ...state,
        dropdownOptions: resolvePayload(action.payload, state.dropdownOptions),
      };

    case MeshPluginActions.SET_HAS_UNSAVED_FIELDS:
      return {
        ...state,
        hasUnsavedFields: resolvePayload(
          action.payload,
          state.hasUnsavedFields,
        ),
      };

    case MeshPluginActions.SET_HAS_UNSAVED_HELP_DOCS:
      return {
        ...state,
        hasUnsavedHelpDocs: resolvePayload(
          action.payload,
          state.hasUnsavedHelpDocs,
        ),
      };

    case MeshPluginActions.SET_HELP_DOCS:
      return {
        ...state,
        helpDocs: resolvePayload(action.payload, state.helpDocs),
      };

    case MeshPluginActions.SET_INPUT_DEFAULT_VALUE:
      return {
        ...state,
        inputDefaultValue: resolvePayload(
          action.payload,
          state.inputDefaultValue,
        ),
      };

    case MeshPluginActions.SET_SAVED_SETTINGS:
      return {
        ...state,
        savedSettings: resolvePayload(action.payload, state.savedSettings),
      };

    case MeshPluginActions.SET_SHOW_FORM:
      return {
        ...state,
        showForm: resolvePayload(action.payload, state.showForm),
      };

    case MeshPluginActions.SET_OPEN_MAPFIELDS_DIALOG:
      return {
        ...state,
        openMapfieldsDialog: resolvePayload(
          action.payload,
          state.openMapfieldsDialog,
        ),
      };

    case MeshPluginActions.SET_HAS_UNSAVED_GENERAL_INFO:
      return {
        ...state,
        hasUnsavedGeneralInfo: resolvePayload(
          action.payload,
          state.hasUnsavedGeneralInfo,
        ),
      };

    case MeshPluginActions.SET_SELECTED_FORM_INDEX:
      return {
        ...state,
        selectedFormIndex: resolvePayload(
          action.payload,
          state.selectedFormIndex,
        ),
      };

    case MeshPluginActions.SET_SELECTED_SETTINGS_PAGE_INDEX:
      return {
        ...state,
        selectedSettingsPageIndex: resolvePayload(
          action.payload,
          state.selectedSettingsPageIndex,
        ),
      };

    case MeshPluginActions.SET_HAS_LOGIC_PAGE:
      return { ...state, hasLogicPage: action.payload };
    case MeshPluginActions.SET_HAS_UI_PAGE:
      return { ...state, hasUIPage: action.payload };
    case MeshPluginActions.SET_HAS_TEMP_CONFIG_PAGE:
      return { ...state, hasTempConfigPage: action.payload };
    default:
      return state;
  }
}
